import { createClient } from '@supabase/supabase-js'
import { buildPersona } from './devPersonas'
import { createMockClient } from './mockSupabaseClient'

const url = import.meta.env.VITE_SUPABASE_URL
const key = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !key) {
  throw new Error('Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY — copy .env.example to .env.local')
}

/**
 * DevPersonaSwitcher.jsx (local dev only) can select a fake persona instead
 * of a real signed-in user — see lib/devPersonas.js and
 * lib/mockSupabaseClient.js. `import.meta.env.DEV` is statically `false` in a
 * production build (`npm run build`), so this whole branch — and the
 * localStorage read that decides it — never runs outside `npm run dev`.
 * There is no code path from a real login into this branch.
 */
let client = createClient(url, key)
if (import.meta.env.DEV) {
  const personaId = localStorage.getItem('beseter.dev_persona')
  const persona = personaId && buildPersona(personaId)
  if (persona) client = createMockClient(persona)
}

export const supabase = client

/**
 * Which social providers this project actually has switched on.
 *
 * signInWithOAuth() is a full-page redirect, so a provider that isn't enabled
 * in the dashboard doesn't come back as an error we can catch — the browser
 * lands on Supabase's raw JSON ("Unsupported provider: provider is not
 * enabled") and the person is just... there, looking at it.
 *
 * /auth/v1/settings is public and says which are on, so the buttons can simply
 * not exist until they'd work. It also means enabling Google in the dashboard
 * makes the button appear on its own, with no deploy.
 */
export async function enabledProviders() {
  try {
    const r = await fetch(`${url}/auth/v1/settings`, { headers: { apikey: key } })
    if (!r.ok) return []
    const s = await r.json()
    return Object.entries(s?.external ?? {})
      .filter(([, on]) => on === true)
      .map(([id]) => id)
  } catch {
    // Offline, or the endpoint moved. Falling back to "none" hides the buttons
    // rather than showing ones that might dead-end.
    return []
  }
}

/** Postgres raises our guard-trigger messages verbatim; they're written to be read. */
export function readableError(error) {
  if (!error) return null
  const m = error.message || String(error)
  if (m.includes('row-level security')) return "You don't have permission to do that."
  if (m.includes('JWT') || m.includes('token')) return 'Your session expired — sign in again.'
  return m.replace(/^.*?:\s*/, '')
}
